import React from 'react';
import * as ContextMenu from '@radix-ui/react-context-menu';
import { FolderPlus, Pencil, Trash2, Copy, FileJson, ArrowRight, Download, Terminal, Settings } from 'lucide-react';
import { useStore, Collection, Folder, CollectionRequest, HttpRequest } from '../../stores';
import { copyCurlToClipboard } from '../../lib/export';

interface CollectionContextMenuProps {
  children: React.ReactNode;
  collection: Collection;
  onAddFolder: () => void;
  onEditCollection: () => void;
  onDeleteCollection: () => void;
  onExportCollection: () => void;
  onOpenSettings?: () => void;
}

export function CollectionContextMenu({
  children,
  collection,
  onAddFolder,
  onEditCollection,
  onDeleteCollection,
  onExportCollection,
  onOpenSettings,
}: CollectionContextMenuProps) {
  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger asChild>{children}</ContextMenu.Trigger>
      <ContextMenu.Portal>
        <ContextMenu.Content className="min-w-[180px] bg-card border border-border rounded-md shadow-lg py-1 z-50">
          <ContextMenu.Item
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-foreground hover:bg-accent cursor-pointer outline-none"
            onClick={onAddFolder}
          >
            <FolderPlus className="w-4 h-4" />
            Add Folder
          </ContextMenu.Item>
          <ContextMenu.Item
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-foreground hover:bg-accent cursor-pointer outline-none"
            onClick={onExportCollection}
          >
            <Download className="w-4 h-4" />
            Export
          </ContextMenu.Item>
          <ContextMenu.Separator className="h-px bg-border my-1" />
          {onOpenSettings && (
            <ContextMenu.Item
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-foreground hover:bg-accent cursor-pointer outline-none"
              onClick={onOpenSettings}
            >
              <Settings className="w-4 h-4" />
              Settings
            </ContextMenu.Item>
          )}
          <ContextMenu.Item
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-foreground hover:bg-accent cursor-pointer outline-none"
            onClick={onEditCollection}
          >
            <Pencil className="w-4 h-4" />
            Edit
          </ContextMenu.Item>
          <ContextMenu.Item
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-500 hover:bg-accent cursor-pointer outline-none"
            onClick={onDeleteCollection}
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </ContextMenu.Item>
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  );
}

interface FolderContextMenuProps {
  children: React.ReactNode;
  collectionId: string;
  folder: Folder;
  onEditFolder: () => void;
  onDeleteFolder: () => void;
}

export function FolderContextMenu({
  children,
  collectionId,
  folder,
  onEditFolder,
  onDeleteFolder,
}: FolderContextMenuProps) {
  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger asChild>{children}</ContextMenu.Trigger>
      <ContextMenu.Portal>
        <ContextMenu.Content className="min-w-[180px] bg-card border border-border rounded-md shadow-lg py-1 z-50">
          <ContextMenu.Item
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-foreground hover:bg-accent cursor-pointer outline-none"
            onClick={onEditFolder}
          >
            <Pencil className="w-4 h-4" />
            Rename
          </ContextMenu.Item>
          <ContextMenu.Item
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-500 hover:bg-accent cursor-pointer outline-none"
            onClick={onDeleteFolder}
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </ContextMenu.Item>
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  );
}

interface RequestContextMenuProps {
  children: React.ReactNode;
  collectionId: string;
  request: CollectionRequest;
  folders: Folder[];
  currentFolderId: string | null;
  fullRequestData?: HttpRequest;
  onMoveToFolder: (targetFolderId: string | null) => void;
  onDeleteRequest: () => void;
  onDuplicateRequest: () => void;
}

export function RequestContextMenu({
  children,
  collectionId,
  request,
  folders,
  currentFolderId,
  fullRequestData,
  onMoveToFolder,
  onDeleteRequest,
  onDuplicateRequest,
}: RequestContextMenuProps) {
  // Filter out the current folder from move options
  const moveOptions = [
    { id: null, name: 'Collection Root' },
    ...folders.filter((f) => f.id !== currentFolderId).map((f) => ({ id: f.id, name: f.name })),
  ];

  // Only show move submenu if there are places to move to
  const showMoveMenu = currentFolderId !== null || folders.length > 0;

  // Handle Copy cURL
  const handleCopyCurl = async () => {
    if (fullRequestData) {
      await copyCurlToClipboard(fullRequestData);
    } else {
      // Create a minimal request from the basic CollectionRequest data
      const minimalRequest: HttpRequest = {
        id: request.id,
        name: request.name,
        method: request.method as HttpRequest['method'],
        url: '',
        headers: [],
        params: [],
      };
      await copyCurlToClipboard(minimalRequest);
    }
  };

  return (
    <ContextMenu.Root>
      <ContextMenu.Trigger asChild>{children}</ContextMenu.Trigger>
      <ContextMenu.Portal>
        <ContextMenu.Content className="min-w-[180px] bg-card border border-border rounded-md shadow-lg py-1 z-50">
          <ContextMenu.Item
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-foreground hover:bg-accent cursor-pointer outline-none"
            onClick={onDuplicateRequest}
          >
            <Copy className="w-4 h-4" />
            Duplicate
          </ContextMenu.Item>
          <ContextMenu.Item
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-foreground hover:bg-accent cursor-pointer outline-none"
            onClick={handleCopyCurl}
          >
            <Terminal className="w-4 h-4" />
            Copy cURL
          </ContextMenu.Item>

          {showMoveMenu && (
            <ContextMenu.Sub>
              <ContextMenu.SubTrigger className="flex items-center justify-between px-3 py-1.5 text-sm text-foreground hover:bg-accent cursor-pointer outline-none">
                <span className="flex items-center gap-2">
                  <ArrowRight className="w-4 h-4" />
                  Move to
                </span>
                <ArrowRight className="w-3 h-3" />
              </ContextMenu.SubTrigger>
              <ContextMenu.Portal>
                <ContextMenu.SubContent className="min-w-[160px] bg-card border border-border rounded-md shadow-lg py-1 z-50">
                  {moveOptions
                    .filter((opt) => opt.id !== currentFolderId)
                    .map((option) => (
                      <ContextMenu.Item
                        key={option.id || 'root'}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm text-foreground hover:bg-accent cursor-pointer outline-none"
                        onClick={() => onMoveToFolder(option.id)}
                      >
                        {option.id ? (
                          <FolderPlus className="w-4 h-4 text-yellow-500" />
                        ) : (
                          <FileJson className="w-4 h-4 text-blue-500" />
                        )}
                        {option.name}
                      </ContextMenu.Item>
                    ))}
                </ContextMenu.SubContent>
              </ContextMenu.Portal>
            </ContextMenu.Sub>
          )}

          <ContextMenu.Separator className="h-px bg-border my-1" />
          <ContextMenu.Item
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-500 hover:bg-accent cursor-pointer outline-none"
            onClick={onDeleteRequest}
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </ContextMenu.Item>
        </ContextMenu.Content>
      </ContextMenu.Portal>
    </ContextMenu.Root>
  );
}
